import React, { useState, useEffect } from 'react';
import LoadingSpinner from "./LoadingSpinner";
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FirebaseService } from '../services/firebaseService';
import AddGroupesElectrogenes from './AddGroupesElectrogenes';
import UpdateGroupesElectrogenes from './UpdateGroupesElectrogenes';
import AddMaterielElectrique from './AddMaterielElectrique';
import UpdateMaterielElectrique from './UpdateMaterielElectrique';
import AdminEclairagePublic from './AdminEclairagePublic';

interface AdminProductManagerProps {
  t: any;
  isRTL: boolean;
}

interface Product {
  descriptionText?: string;
  [key: string]: any;
  id?: string;
  name: string;
  mainImage: string;
  provider: string;
  description: string; // Change back to string only
  technicalSpecs: { [key: string]: string };
  additionalImages: string[];
  category: string;
  createdAt?: any;
  updatedAt?: any;
}

const ProductManagerContainer = styled.div<{ $isRTL: boolean }>`
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
`;

const Title = styled.h2<{ $isRTL: boolean }>`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const Subtitle = styled.p<{ $isRTL?: boolean }>`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0.5rem 0 0 0;
`;

const AddButton = styled(motion.button)<{ $isRTL?: boolean }>`
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ProductCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const ProductImage = styled.div`
  width: 100%;
  height: 200px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProductContent = styled.div`
  padding: 1.5rem;
`;

const ProductName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
`;

const ProductProvider = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1rem 0;
`;

const ProductDescription = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
  margin-bottom: 1rem;
  
  ul {
    margin: 0;
    padding-left: 1rem;
  }
  
  li {
    margin-bottom: 0.25rem;
  }
`;

const ProductActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button<{ $variant: 'edit' | 'delete' }>`
  flex: 1;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$variant === 'edit' ? `
    background: #dc2626;
    color: white;
    
    &:hover {
      background: #b91c1c;
    }
  ` : `
    background: #ef4444;
    color: white;
    
    &:hover {
      background: #dc2626;
    }
  `}
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled(motion.div)<{ $isRTL: boolean }>`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  
  &:hover {
    color: #374151;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
`;

const FileInput = styled.input`
  padding: 0.75rem;
  border: 2px dashed #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #dc2626;
    background: #fef2f2;
  }
  
  &:focus {
    outline: none;
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
`;

const ImagePreviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  
  &:hover {
    background: #dc2626;
  }
`;

const UploadProgress = styled.div<{ $progress: number }>`
  width: 100%;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 0.5rem;
  
  &::after {
    content: '';
    display: block;
    width: ${props => props.$progress}%;
    height: 100%;
    background: #dc2626;
    transition: width 0.3s ease;
  }
`;

const ProgressText = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0.5rem 0 0 0;
  text-align: center;
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
`;

const SpecsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SpecRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const SpecInput = styled(Input)`
  flex: 1;
`;

const RemoveButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  
  &:hover {
    background: #dc2626;
  }
`;

const AddSpecButton = styled.button`
  background: #10b981;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  align-self: flex-start;
  
  &:hover {
    background: #059669;
  }
`;

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
const BulletContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
const BulletRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
const BulletInput = styled(Input)`
  flex: 1;
`;

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
const AddBulletButton = styled.button`
  background: #10b981;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  align-self: flex-start;
  
  &:hover {
    background: #059669;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
`;

const EmptyDescription = styled.p`
  color: #6b7280;
  margin: 0;
`;

const AdminProductManager: React.FC<AdminProductManagerProps> = ({ t, isRTL }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState<Product>({
    name: '',
    mainImage: '',
    provider: '',
    description: '',
    technicalSpecs: {},
    additionalImages: [],
    category: '0'
  });

  // File states
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState<File[]>([]);
  const [mainImagePreview, setMainImagePreview] = useState<string>('');
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState<string[]>([]);

  // Available partners
  const partners = [
    { value: 'nexans', label: 'Nexans' },
    { value: 'deutz', label: 'DEUTZ' },
    { value: 'ines', label: 'Ines Max' },
    { value: 'cahors', label: 'CAHORS' },
    { value: 'ifamel', label: 'IFAMEL' }
  ];

  // Available categories (removed Groupes Electrogènes)
  const categories = [
    { value: '0', label: 'Materiel & Equipment de Distribution Electrique' },
    { value: '1', label: 'Groupes Électrogènes' },
    { value: '2', label: 'Matériel Électrique' }
  ];

  // Add state for the new modals
  const [showAddGroupesElectrogenes, setShowAddGroupesElectrogenes] = useState(false);
  const [showUpdateGroupesElectrogenes, setShowUpdateGroupesElectrogenes] = useState(false);
  const [editingGroupesElectrogenesId, setEditingGroupesElectrogenesId] = useState<string | null>(null);
  const [showAddMaterielElectrique, setShowAddMaterielElectrique] = useState(false);
  const [showUpdateMaterielElectrique, setShowUpdateMaterielElectrique] = useState(false);
  const [editingMaterielElectriqueId, setEditingMaterielElectriqueId] = useState<string | null>(null);
  const [showAddEclairagePublic, setShowAddEclairagePublic] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await FirebaseService.getAll('products');
      setProducts((data || []) as Product[]);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAddProduct = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      mainImage: '',
      provider: '',
      description: '',
      technicalSpecs: {},
      additionalImages: [],
      category: '0'
    });
    // Reset file states
    setMainImageFile(null);
    setAdditionalImageFiles([]);
    setMainImagePreview('');
    setAdditionalImagePreviews([]);
    setUploadProgress(0);
    setIsUploading(false);
    setIsModalOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    if (product.category === '1') {
      // Handle Groupes Electrogènes separately
      setEditingGroupesElectrogenesId(product.id!);
      setShowUpdateGroupesElectrogenes(true);
    } else if (product.category === '2') {
      // Handle Matériel Électrique separately
      setEditingMaterielElectriqueId(product.id!);
      setShowUpdateMaterielElectrique(true);
    } else {
      // Handle other categories normally
      setEditingProduct(product);
      setFormData({
        name: product.name,
        mainImage: product.mainImage,
        provider: product.provider,
        description: product.description,
        technicalSpecs: product.technicalSpecs,
        additionalImages: product.additionalImages,
        category: product.category
      });
      setIsModalOpen(true);
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await FirebaseService.delete('products', productId);
        await loadProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMainImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setMainImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAdditionalImageFiles(prev => [...prev, ...files]);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAdditionalImagePreviews(prev => [...prev, e.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeMainImage = () => {
    setMainImageFile(null);
    setMainImagePreview('');
    setFormData(prev => ({ ...prev, mainImage: '' }));
  };

  const removeAdditionalImage = (index: number) => {
    setAdditionalImageFiles(prev => prev.filter((_, i) => i !== index));
    setAdditionalImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async () => {
    const uploadedImages: string[] = [];
    let mainImageUrl = formData.mainImage;
    
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Upload main image if new file is selected
      if (mainImageFile) {
        const mainImagePath = `products/${Date.now()}_main_${mainImageFile.name}`;
        console.log('📤 Uploading main image:', mainImagePath);
        console.log('📁 File details:', {
          name: mainImageFile.name,
          size: mainImageFile.size,
          type: mainImageFile.type
        });
        
        mainImageUrl = await FirebaseService.uploadFile(mainImagePath, mainImageFile);
        setUploadProgress(30);
        console.log('✅ Main image uploaded:', mainImageUrl);
      }

      // Upload additional images
      if (additionalImageFiles.length > 0) {
        for (let i = 0; i < additionalImageFiles.length; i++) {
          const file = additionalImageFiles[i];
          const imagePath = `products/${Date.now()}_additional_${i}_${file.name}`;
          console.log('📤 Uploading additional image:', imagePath);
          console.log('📁 File details:', {
            name: file.name,
            size: file.size,
            type: file.type
          });
          
          const imageUrl = await FirebaseService.uploadFile(imagePath, file);
          uploadedImages.push(imageUrl);
          setUploadProgress(30 + ((i + 1) / additionalImageFiles.length) * 60);
          console.log('✅ Additional image uploaded:', imageUrl);
        }
      }

      // Combine existing additional images with new ones
      const allAdditionalImages = [...(formData.additionalImages || []), ...uploadedImages];

      setUploadProgress(100);
      return { mainImageUrl, additionalImages: allAdditionalImages };
    } catch (error) {
      console.error('❌ Error uploading images:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      throw new Error(`Image upload failed: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Upload images first if there are new files
      let mainImageUrl = formData.mainImage;
      let additionalImages = formData.additionalImages || [];

      if (mainImageFile || additionalImageFiles.length > 0) {
        try {
          const uploadResult = await uploadImages();
          mainImageUrl = uploadResult.mainImageUrl;
          additionalImages = uploadResult.additionalImages;
        } catch (uploadError) {
          console.error('Upload failed:', uploadError);
          const errorMessage = uploadError instanceof Error ? uploadError.message : 'Unknown upload error';
          alert(`Image upload failed: ${errorMessage}\n\nPlease check:\n1. Firebase Storage rules are configured\n2. CORS is properly set up\n3. You are authenticated`);
          return;
        }
      }
      
      // No need to filter description since it's now a string
      const productData = {
        ...formData,
        mainImage: mainImageUrl,
        additionalImages,
        description: formData.description // Keep as string
      };

      console.log('💾 Saving product data:', productData);

      if (editingProduct) {
        await FirebaseService.update('products', editingProduct.id!, productData);
      } else {
        await FirebaseService.create('products', productData);
      }
      
      console.log('✅ Product saved successfully');
      setIsModalOpen(false);
      await loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Error saving product: ${errorMessage}\n\nPlease check:\n1. Firestore security rules allow write access\n2. You are properly authenticated`);
    } finally {
      setLoading(false);
      setUploadProgress(0);
      setIsUploading(false);
    }
  };

  const addSpec = () => {
    setFormData(prev => ({
      ...prev,
      technicalSpecs: { ...prev.technicalSpecs, '': '' }
    }));
  };

  const updateSpec = (oldKey: string, newKey: string, value: string) => {
    setFormData(prev => {
      const newSpecs = { ...prev.technicalSpecs };
      if (oldKey !== newKey) {
        delete newSpecs[oldKey];
      }
      newSpecs[newKey] = value;
      return { ...prev, technicalSpecs: newSpecs };
    });
  };

  const removeSpec = (key: string) => {
    setFormData(prev => {
      const newSpecs = { ...prev.technicalSpecs };
      delete newSpecs[key];
      return { ...prev, technicalSpecs: newSpecs };
    });
  };

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  const addBullet = () => {
    // Remove this function since we're not using bullet points anymore
    // Description is now a single text field
  };

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  const updateBullet = (index: number, value: string) => {
    // Remove this function since we're not using bullet points anymore
  };

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  const removeBullet = (index: number) => {
    // Remove this function since we're not using bullet points anymore
  };

  return (
    <ProductManagerContainer $isRTL={isRTL}>
      <Header>
        <Title $isRTL={isRTL}>Product Management</Title>
        <Subtitle $isRTL={isRTL}>Manage your product catalog</Subtitle>
      </Header>

      <ActionsContainer>
        <AddButton
          $isRTL={isRTL}
          onClick={() => setIsModalOpen(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          + Add Product
        </AddButton>
        
        <AddButton
          $isRTL={isRTL}
          onClick={() => setShowAddGroupesElectrogenes(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}
        >
          + Add Groupes Electrogènes
        </AddButton>

        <AddButton
          $isRTL={isRTL}
          onClick={() => setShowAddMaterielElectrique(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' }}
        >
          + Add Matériel Électrique
        </AddButton>
        <AddButton
          $isRTL={isRTL}
          onClick={() => setShowAddEclairagePublic(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
        >
          + Add Éclairage Public
        </AddButton>
      </ActionsContainer>

      {loading && products.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <LoadingSpinner />
        </div>
      ) : products.length === 0 ? (
        <EmptyState>
          <EmptyIcon>📦</EmptyIcon>
          <EmptyTitle>No Products</EmptyTitle>
          <EmptyDescription>Start by adding your first product</EmptyDescription>
        </EmptyState>
      ) : (
        <ProductsGrid>
          {products.map((product, index) => (
            <ProductCard
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <ProductImage>
                {product.mainImage ? (
                  <img src={product.mainImage} alt={product.name} />
                ) : (
                  <div style={{ color: '#9ca3af', fontSize: '3rem' }}>📷</div>
                )}
              </ProductImage>
              <ProductContent>
                <ProductName>{product.name}</ProductName>
                <ProductProvider>Provider: {product.provider}</ProductProvider>
                <ProductDescription>
                  {product.description ? (
                    <p>
                      {product.description.length > 100 
                        ? `${product.description.slice(0, 100)}...` 
                        : product.description
                      }
                    </p>
                  ) : (
                    <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>No description available</p>
                  )}
                </ProductDescription>
                <ProductActions>
                  <ActionButton
                    $variant="edit"
                    onClick={() => handleEditProduct(product)}
                  >
                    Edit
                  </ActionButton>
                  <ActionButton
                    $variant="delete"
                    onClick={() => handleDeleteProduct(product.id!)}
                  >
                    Delete
                  </ActionButton>
                </ProductActions>
              </ProductContent>
            </ProductCard>
          ))}
        </ProductsGrid>
      )}

      {/* Existing Add/Edit Product Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsModalOpen(false)}
          >
            <Modal
              $isRTL={isRTL}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </ModalTitle>
                <CloseButton onClick={() => setIsModalOpen(false)}>
                  ×
                </CloseButton>
              </ModalHeader>

              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>Product Name</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Category</Label>
                  <Select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Provider</Label>
                  <Select
                    value={formData.provider}
                    onChange={(e) => setFormData(prev => ({ ...prev, provider: e.target.value }))}
                    required
                  >
                    <option value="">Select a provider</option>
                    {partners.map(partner => (
                      <option key={partner.value} value={partner.value}>{partner.label}</option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Main Image</Label>
                  <FileInput
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageChange}
                  />
                  {mainImagePreview && (
                    <ImagePreviewContainer>
                      <ImagePreview>
                        <img src={mainImagePreview} alt="Main preview" />
                        <RemoveImageButton type="button" onClick={removeMainImage}>
                          ×
                        </RemoveImageButton>
                      </ImagePreview>
                    </ImagePreviewContainer>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Additional Images</Label>
                  <FileInput
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleAdditionalImagesChange}
                  />
                  {additionalImagePreviews.length > 0 && (
                    <ImagePreviewContainer>
                      {additionalImagePreviews.map((preview, index) => (
                        <ImagePreview key={index}>
                          <img src={preview} alt={`Additional preview ${index + 1}`} />
                          <RemoveImageButton 
                            type="button" 
                            onClick={() => removeAdditionalImage(index)}
                          >
                            ×
                          </RemoveImageButton>
                        </ImagePreview>
                      ))}
                    </ImagePreviewContainer>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Description</Label>
                  <Input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter product description"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Technical Specifications</Label>
                  <SpecsContainer>
                    {Object.entries(formData.technicalSpecs).map(([key, value], index) => (
                      <SpecRow key={index}>
                        <SpecInput
                          type="text"
                          value={key}
                          onChange={(e) => updateSpec(key, e.target.value, value)}
                          placeholder="Specification name"
                        />
                        <SpecInput
                          type="text"
                          value={value}
                          onChange={(e) => updateSpec(key, key, e.target.value)}
                          placeholder="Value"
                        />
                        <RemoveButton
                          type="button"
                          onClick={() => removeSpec(key)}
                        >
                          Remove
                        </RemoveButton>
                      </SpecRow>
                    ))}
                    <AddSpecButton type="button" onClick={addSpec}>
                      + Add Specification
                    </AddSpecButton>
                  </SpecsContainer>
                </FormGroup>

                {isUploading && (
                  <FormGroup>
                    <UploadProgress $progress={uploadProgress} />
                    <ProgressText>Uploading images... {uploadProgress}%</ProgressText>
                  </FormGroup>
                )}

                <SubmitButton type="submit" disabled={loading || isUploading}>
                  {loading || isUploading 
                    ? 'Processing...' 
                    : (editingProduct ? 'Update Product' : 'Create Product')
                  }
                </SubmitButton>
              </Form>
            </Modal>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Add Groupes Electrogènes Modal */}
      {showAddGroupesElectrogenes && (
        <AddGroupesElectrogenes
          t={t}
          isRTL={isRTL}
          onClose={() => setShowAddGroupesElectrogenes(false)}
          onSuccess={() => {
            setShowAddGroupesElectrogenes(false);
            loadProducts();
          }}
        />
      )}

      {/* Update Groupes Electrogènes Modal */}
      {showUpdateGroupesElectrogenes && editingGroupesElectrogenesId && (
        <UpdateGroupesElectrogenes
          t={t}
          isRTL={isRTL}
          productId={editingGroupesElectrogenesId}
          onClose={() => {
            setShowUpdateGroupesElectrogenes(false);
            setEditingGroupesElectrogenesId(null);
          }}
          onSuccess={() => {
            setShowUpdateGroupesElectrogenes(false);
            setEditingGroupesElectrogenesId(null);
            loadProducts();
          }}
        />
      )}

      {/* Add Matériel Électrique Modal */}
      {showAddMaterielElectrique && (
        <AddMaterielElectrique
          t={t}
          isRTL={isRTL}
          onClose={() => setShowAddMaterielElectrique(false)}
          onSuccess={() => {
            setShowAddMaterielElectrique(false);
            loadProducts();
          }}
        />
      )}

      {/* Update Matériel Électrique Modal */}
      {showUpdateMaterielElectrique && editingMaterielElectriqueId && (
        <UpdateMaterielElectrique
          t={t}
          isRTL={isRTL}
          productId={editingMaterielElectriqueId}
          onClose={() => {
            setShowUpdateMaterielElectrique(false);
            setEditingMaterielElectriqueId(null);
          }}
          onSuccess={() => {
            setShowUpdateMaterielElectrique(false);
            setEditingMaterielElectriqueId(null);
            loadProducts();
          }}
        />
      )}
      {/* Add Éclairage Public Modal */}
      {showAddEclairagePublic && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto', position: 'relative' }}>
            <button
              onClick={() => setShowAddEclairagePublic(false)}
              style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
            >
              ×
            </button>
            <AdminEclairagePublic
              t={t}
              isRTL={isRTL}
              formData={{}}
              onInputChange={() => {}}
            />
          </div>
        </div>
      )}
    </ProductManagerContainer>
  );
};

export default AdminProductManager;
